<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { ref, reactive, computed, onMounted, nextTick, watch, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import type { FormInstance } from 'element-plus'
import { vMaska } from 'maska/vue'
import { usePSUModelOptions } from '@/composables/rc/power-supply-model-options'
import { useMinerModelOptions } from '@/composables/rc/miner-model-options'
import { useMiners } from '@/composables/rc/miners'
import { flattenObject } from '@/util'
import type { IModel } from '@/types/rc/miners'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { NotifySuccess } from '@/helpers/core/notifications'

// ==================== Component Props & Emits ====================
const modelValue = defineModel<boolean>()
const props = defineProps<{
  containerId: number
}>()

// ==================== API & Data Fetching ====================
const psuModelOptionsQuery = usePSUModelOptions()
const psuModelOptions = computed(() => psuModelOptionsQuery.data.value ?? [])

const {
  minerModelNameOptions,
  isFetching: minerModelsFetching,
  isRefetching: minerModelsRefetching,
} = useMinerModelOptions()

const { createMinerMutation, deleteMinerMutation, bulkDeleteMinersMutation, updateMinerMutation } = useMiners()

// ==================== Form & UI State ====================
// Form data and validation
const form = reactive({
  model: '',
  serial_number: '',
  asset_tag: '',
  mac_address: '',
  psu_model: undefined as IModel | undefined,
  psu_serial_number: '',
})

const formRules = {
  model: [{ required: true, message: 'Miner model is required', trigger: 'change' }],
  serial_number: [{ required: true, message: 'Serial number is required', trigger: 'change' }],
}

// Allows fields to be locked/preserved between entries
const lockState = reactive({
  model: false,
  asset_tag: true,
  psu_model: true,
  psu_serial_number: true,
  mac_address: true,
})

// List of miners being added
const minersList = ref<any[]>([])
const deleteLoadingIndex = ref<number>()

// DOM/UI references and state
const formRef = ref<FormInstance>()
const minerModelRef = ref()
const serialNumberRef = ref()
const minerModelDropdownVisible = ref(false)
const psuModelDropdownVisible = ref(false)
const maxTableHeight = ref('500px')
const tableRef = ref<HTMLElement | null>(null)

// Table editing state
const editingCellInfo = ref<{ rowIndex: number; field: string; value: any } | null>(null)
const editInput = ref()

// ==================== Computed Properties ====================
const isFormLoading = computed(
  () =>
    minerModelsFetching.value ||
    minerModelsRefetching.value ||
    psuModelOptionsQuery.isFetching.value ||
    createMinerMutation.isPending.value
)

const hasEntries = computed(() => minersList.value.length > 0)

const isFormInvalid = computed(() => {
  // Check required fields
  return !form.model || !form.serial_number
})

// ==================== Form Functions ====================
const resetFormExceptLocked = () => {
  if (!lockState.model) form.model = ''
  if (!lockState.psu_model) form.psu_model = undefined
  if (!lockState.asset_tag) form.asset_tag = ''
  if (!lockState.psu_serial_number) form.psu_serial_number = ''
  if (!lockState.mac_address) form.mac_address = ''

  // Serial number always resets as it's required for each entry
  form.serial_number = ''

  setTimeout(() => {
    formRef.value?.clearValidate()
  }, 100)
  focusFirstUnlockedField()
}

const focusFirstUnlockedField = () => {
  // setTimeout required to ensure focus is applied after DOM update
  setTimeout(() => {
    if (!lockState.model && minerModelRef.value) {
      minerModelRef.value.focus()
    } else {
      serialNumberRef.value?.focus()
    }
  }, 100)
}

// ==================== Miner CRUD Operations ====================
const addMinerToList = async () => {
  if (!formRef.value) return

  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const createMinerPayload = {
          container_id: props.containerId,
          model: form.model,
          serial_number: form.serial_number,
          asset_tag: form.asset_tag || undefined,
          mac_address: form.mac_address || undefined,
          ...(form.psu_model && {
            psu_model: form.psu_model.name,
            psu_model_version: form.psu_model.version,
          }),
          psu_serial_number: form.psu_serial_number || undefined,
        }

        await createMinerMutation.mutateAsync(createMinerPayload, {
          onSuccess: (data) => {
            const flattenedData = flattenObject(data) as any
            minersList.value.unshift(flattenedData)

            NotifySuccess(`Added miner with serial number "${flattenedData.sn || ''}"`, {
              title: 'Added Miner',
              position: 'bottom-right',
            })
            resetFormExceptLocked()
          },
        })
      } catch {
        // Error handled by mutation
      }
    }
  })
}

const handleDeleteMiner = (index: number, miner: any) => {
  DeleteConfirmationModal(
    'Delete Miner',
    `Are you sure you want to delete miner with SN "${miner.sn}"? This action cannot be undone.`,
    miner.id,
    async (id) => {
      try {
        deleteLoadingIndex.value = index
        await deleteMinerMutation.mutateAsync(id)
        return true
      } finally {
        deleteLoadingIndex.value = undefined
      }
    },
    () => {
      minersList.value.splice(index, 1)
    }
  )
}

const confirmDiscardChanges = () => {
  if (!hasEntries.value) {
    return (modelValue.value = false)
  }

  const minerIds = minersList.value.map((miner) => miner.id)
  const minersCount = minersList.value.length
  const confirmMessage = `Are you sure you want to delete ${minersCount} miner${minersCount !== 1 ? 's' : ''}? This action cannot be undone.`

  DeleteConfirmationModal(
    'Discard Changes',
    confirmMessage,
    minerIds,
    async (ids) => {
      try {
        await bulkDeleteMinersMutation.mutateAsync(ids)
        return true
      } catch {
        return false
      }
    },
    () => {
      minersList.value = []
    }
  )
}

// ==================== Table Editing Functions ====================
const startEditCell = (rowIndex: number, field: string, value: any) => {
  editingCellInfo.value = { rowIndex, field, value }
  setTimeout(() => {
    if (editInput.value) {
      editInput.value.focus()
    }
  })
}

const saveEdit = async (rowIndex: number) => {
  if (!editingCellInfo.value) return

  const miner = minersList.value[rowIndex]
  const { field, value } = editingCellInfo.value

  if (miner[field] === value) {
    editingCellInfo.value = null
    return
  }

  if (['model', 'sn'].includes(field) && !value) {
    ElMessage.error(`${field === 'sn' ? 'Serial number' : 'Model'} is required`)
    editingCellInfo.value = null
    return
  }

  if (miner.id) {
    // Update existing miner in backend
    const updatePayload: any = { id: miner.id }

    if (field === 'psu.model') {
      updatePayload.psu = {
        model: {
          name: value?.name || null,
          version: value?.version || null,
        },
      }
    } else {
      updatePayload[field] = value
    }

    try {
      await updateMinerMutation.mutateAsync(updatePayload)
      miner[field] = value
    } catch {
      // Error handled by mutation
    }
  } else {
    // Update local miner data
    miner[field] = value
  }

  editingCellInfo.value = null
}

const cancelEdit = () => {
  editingCellInfo.value = null
}

const onEditKeydown = (event: KeyboardEvent | Event, rowIndex: number) => {
  if (!('key' in event)) return
  if (event.key === 'Enter') {
    saveEdit(rowIndex)
  } else if (event.key === 'Escape') {
    cancelEdit()
  }
}

// ==================== Event Handlers ====================
const handleFormKeydown = (evt: KeyboardEvent) => {
  // Only handle Tab/Enter for auto-submission
  if (evt.key !== 'Tab' && evt.key !== 'Enter') return
  if (editingCellInfo.value !== null) return

  // Don't intercept events for UI controls that need keyboard navigation
  const target = evt.target as HTMLElement
  if (target.tagName === 'BUTTON' || target.closest('.el-button') || target.closest('.el-select-dropdown')) return
  if (minerModelDropdownVisible.value || psuModelDropdownVisible.value) return
  if (createMinerMutation.isPending.value) return

  // Check all required fields and optional fields validity
  const requiredFieldsFilled = formRef.value?.validate()
  const assetTagValid = !!form.asset_tag || lockState.asset_tag
  const psuModelValid = !!form.psu_model || lockState.psu_model
  const psuSerialValid = !!form.psu_serial_number || lockState.psu_serial_number
  const macAddressValid = !!form.mac_address || lockState.mac_address

  // Auto-submit form if all validation passes
  if (requiredFieldsFilled && assetTagValid && psuModelValid && psuSerialValid && macAddressValid) {
    addMinerToList()
  }
}

const handleCloseDialog = () => {
  modelValue.value = false
}

const handleDialogClosed = () => {
  formRef.value?.resetFields()
  minersList.value = []
  Object.keys(lockState).forEach((key) => (lockState[key as keyof typeof lockState] = key === 'model' ? false : true))
  editingCellInfo.value = null
}

// ==================== Dropdown Selection ====================
// Handles Tab key press during dropdown selection to improve keyboard navigation
const selectOption = (event: Event) => {
  const isDropdownVisible = minerModelDropdownVisible.value || psuModelDropdownVisible.value
  if (!isDropdownVisible) return

  let popperClass
  if (minerModelDropdownVisible.value) {
    popperClass = 'scan-miners-dialog-miner-model-popper'
  } else if (psuModelDropdownVisible.value) {
    popperClass = 'scan-miners-dialog-psu-model-popper'
  }

  // Find and click the highlighted option
  let option = document.querySelector(`.${popperClass} .el-select-dropdown__item.is-hovering`)
  // If there is no hovered option, check if there is a single .el-select-dropdown__item, which means an exact match.
  if (!option) {
    // Element Plus library filters out options by adding a style attribute with display: none
    const items = document.querySelectorAll(`.${popperClass} .el-select-dropdown__item:not([style="display: none;"])`)
    if (items.length === 1) {
      option = items[0] as HTMLElement
    }
  }
  if (option) {
    event.preventDefault()
    ;(option as HTMLElement).click()

    // Move focus to next field after selection
    setTimeout(() => {
      if (event.target === minerModelRef.value) {
        serialNumberRef.value?.focus()
      } else {
        // Find the next input field for better form flow
        const formElements = formRef.value?.$el.querySelectorAll('input, select')
        const currentIndex = Array.from(formElements || []).findIndex(
          (el) => el === event.target || el === (event.target as HTMLElement)?.closest('.el-select')
        )

        if (currentIndex >= 0 && currentIndex < (formElements?.length || 0) - 1) {
          ;(formElements?.[currentIndex + 1] as HTMLElement)?.focus()
        }
      }
    }, 0)
  }
}

// ==================== Layout & Resize Handling ====================
// Dynamically adjust table height based on container size
const updateTableHeight = () => {
  if (tableRef.value && tableRef.value.getBoundingClientRect().height) {
    maxTableHeight.value = `${tableRef.value.getBoundingClientRect().height}px`
  }
}

// ==================== Watchers ====================
// Update table height when dialog becomes visible
watch(
  () => modelValue.value,
  async (isVisible) => {
    if (isVisible) {
      await nextTick()
      updateTableHeight()
      focusFirstUnlockedField()
    }
  },
  { immediate: true }
)

// Observe table element when available
watch(tableRef, (el) => {
  if (resizeObserver && el) {
    resizeObserver.observe(el)
  }
})

// Set up resize handling
let resizeObserver: ResizeObserver | null = null

// ==================== Lifecycle Hooks ====================
onMounted(() => {
  // ResizeObserver gives more accurate size updates than window resize events
  resizeObserver = new ResizeObserver(() => {
    if (modelValue.value) {
      updateTableHeight()
    }
  })

  window.addEventListener('resize', updateTableHeight)
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  window.removeEventListener('resize', updateTableHeight)
})
</script>

<template>
  <el-dialog
    v-model="modelValue"
    v-loading="isFormLoading"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :show-close="false"
    title="Add Miners"
    width="95%"
    class="scan-miners-dialog"
    align-center
    @closed="handleDialogClosed"
  >
    <template #header>
      <div class="scan-miners-dialog__header">
        <h3 class="scan-miners-dialog__header-title">Add Miners</h3>
        <div class="scan-miners-dialog__header-buttons">
          <el-button tabindex="-1" link @click="handleCloseDialog">
            <Icon icon="material-symbols:close" :height="20" />
          </el-button>
        </div>
      </div>
    </template>

    <div class="scan-miners-dialog__content">
      <!-- Left Column - Form -->
      <div class="scan-miners-dialog__form">
        <el-scrollbar max-height="75vh">
          <el-form
            ref="formRef"
            v-loading="isFormLoading"
            :model="form"
            :rules="formRules"
            scroll-to-error
            label-position="top"
            label-width="auto"
            class="scan-miners-dialog__form-content"
            @keydown="handleFormKeydown"
          >
            <el-form-item label="Miner Model" prop="model">
              <div class="scan-miners-dialog__field-group">
                <el-select
                  ref="minerModelRef"
                  v-model="form.model"
                  placeholder="Select Miner Model"
                  filterable
                  :disabled="lockState.model"
                  class="scan-miners-dialog__field-input"
                  popper-class="scan-miners-dialog-miner-model-popper"
                  @keydown.tab="selectOption"
                  @visible-change="minerModelDropdownVisible = $event"
                >
                  <el-option
                    v-for="option in minerModelNameOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  />
                </el-select>
                <el-button
                  tabindex="-1"
                  type="default"
                  class="scan-miners-dialog__lock-button"
                  :class="{ 'is-locked': lockState.model }"
                  @click="lockState.model = !lockState.model"
                >
                  <Icon :icon="lockState.model ? 'material-symbols:lock' : 'material-symbols:lock-open'" :height="16" />
                </el-button>
              </div>
            </el-form-item>

            <el-form-item label="Serial Number" prop="serial_number">
              <el-input ref="serialNumberRef" v-model="form.serial_number" placeholder="Enter Serial Number" />
            </el-form-item>

            <el-form-item label="Asset Tag" prop="asset_tag">
              <div class="scan-miners-dialog__field-group">
                <el-input
                  v-model="form.asset_tag"
                  placeholder="Enter Asset Tag (Optional)"
                  :disabled="lockState.asset_tag"
                  class="scan-miners-dialog__field-input"
                />
                <el-button
                  tabindex="-1"
                  type="default"
                  class="scan-miners-dialog__lock-button"
                  :class="{ 'is-locked': lockState.asset_tag }"
                  @click="lockState.asset_tag = !lockState.asset_tag"
                >
                  <Icon
                    :icon="lockState.asset_tag ? 'material-symbols:lock' : 'material-symbols:lock-open'"
                    :height="16"
                  />
                </el-button>
              </div>
            </el-form-item>

            <el-form-item label="PSU Model" prop="psu_model">
              <div class="scan-miners-dialog__field-group">
                <el-select
                  v-model="form.psu_model"
                  placeholder="Select PSU Model (Optional)"
                  filterable
                  clearable
                  :disabled="lockState.psu_model"
                  value-key="valueKey"
                  class="scan-miners-dialog__field-input"
                  popper-class="scan-miners-dialog-psu-model-popper"
                  @keydown.tab="selectOption"
                  @visible-change="psuModelDropdownVisible = $event"
                >
                  <el-option
                    v-for="option in psuModelOptions"
                    :key="option.value.valueKey"
                    :label="option.label"
                    :value="option.value"
                  >
                    <div class="w-full inline-flex justify-between items-center">
                      <p>{{ option.value.name }}</p>
                      <small v-if="option.value?.version" class="text-tertiary">{{ option.value.version }}</small>
                    </div>
                  </el-option>
                </el-select>
                <el-button
                  tabindex="-1"
                  type="default"
                  class="scan-miners-dialog__lock-button"
                  :class="{ 'is-locked': lockState.psu_model }"
                  @click="lockState.psu_model = !lockState.psu_model"
                >
                  <Icon
                    :icon="lockState.psu_model ? 'material-symbols:lock' : 'material-symbols:lock-open'"
                    :height="16"
                  />
                </el-button>
              </div>
            </el-form-item>

            <el-form-item label="PSU Serial Number" prop="psu_serial_number">
              <div class="scan-miners-dialog__field-group">
                <el-input
                  v-model="form.psu_serial_number"
                  placeholder="Enter PSU Serial Number (Optional)"
                  :disabled="lockState.psu_serial_number"
                  class="scan-miners-dialog__field-input"
                />
                <el-button
                  tabindex="-1"
                  type="default"
                  class="scan-miners-dialog__lock-button"
                  :class="{ 'is-locked': lockState.psu_serial_number }"
                  @click="lockState.psu_serial_number = !lockState.psu_serial_number"
                >
                  <Icon
                    :icon="lockState.psu_serial_number ? 'material-symbols:lock' : 'material-symbols:lock-open'"
                    :height="16"
                  />
                </el-button>
              </div>
            </el-form-item>

            <el-form-item label="MAC Address" prop="mac_address">
              <div class="scan-miners-dialog__field-group">
                <el-input
                  v-model="form.mac_address"
                  v-maska
                  placeholder="Enter MAC Address (Optional)"
                  data-maska="##:##:##:##:##:##"
                  data-maska-tokens="#:[0-9a-fA-F]"
                  :disabled="lockState.mac_address"
                  class="scan-miners-dialog__field-input"
                />
                <el-button
                  tabindex="-1"
                  type="default"
                  class="scan-miners-dialog__lock-button"
                  :class="{ 'is-locked': lockState.mac_address }"
                  @click="lockState.mac_address = !lockState.mac_address"
                >
                  <Icon
                    :icon="lockState.mac_address ? 'material-symbols:lock' : 'material-symbols:lock-open'"
                    :height="16"
                  />
                </el-button>
              </div>
            </el-form-item>
          </el-form>
        </el-scrollbar>
        <el-button
          class="scan-miners-dialog__form-add-button"
          type="primary"
          :loading="createMinerMutation.isPending.value"
          :disabled="createMinerMutation.isPending.value || isFormInvalid"
          @click="addMinerToList"
        >
          Add Miner
        </el-button>
      </div>

      <!-- Right Column - Table -->
      <div ref="tableRef" class="scan-miners-dialog__table">
        <el-table :data="minersList" style="width: 100%" height="100%" :max-height="maxTableHeight">
          <template #empty>
            <el-empty />
          </template>
          <el-table-column prop="model" label="Miner Model" min-width="120">
            <template #default="scope">
              <div
                class="scan-miners-dialog__editable-cell"
                @click="startEditCell(scope.$index, 'model', scope.row.model)"
              >
                <template
                  v-if="
                    editingCellInfo && editingCellInfo.rowIndex === scope.$index && editingCellInfo.field === 'model'
                  "
                >
                  <el-select
                    ref="editInput"
                    v-model="editingCellInfo.value"
                    filterable
                    size="small"
                    style="width: 100%"
                    @blur="saveEdit(scope.$index)"
                    @keydown="onEditKeydown($event, scope.$index)"
                  >
                    <el-option
                      v-for="option in minerModelNameOptions"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                    />
                  </el-select>
                </template>
                <span v-else>{{ scope.row.model }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="sn" label="Serial Number" min-width="120">
            <template #default="scope">
              <div class="scan-miners-dialog__editable-cell" @click="startEditCell(scope.$index, 'sn', scope.row.sn)">
                <template
                  v-if="editingCellInfo && editingCellInfo.rowIndex === scope.$index && editingCellInfo.field === 'sn'"
                >
                  <el-input
                    ref="editInput"
                    v-model="editingCellInfo.value"
                    size="small"
                    @blur="saveEdit(scope.$index)"
                    @keydown="onEditKeydown($event, scope.$index)"
                  />
                </template>
                <span v-else>{{ scope.row.sn }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="asset_tag" label="Asset Tag" min-width="120">
            <template #default="scope">
              <div
                class="scan-miners-dialog__editable-cell"
                @click="startEditCell(scope.$index, 'asset_tag', scope.row.asset_tag)"
              >
                <template
                  v-if="
                    editingCellInfo &&
                    editingCellInfo.rowIndex === scope.$index &&
                    editingCellInfo.field === 'asset_tag'
                  "
                >
                  <el-input
                    ref="editInput"
                    v-model="editingCellInfo.value"
                    size="small"
                    @blur="saveEdit(scope.$index)"
                    @keydown="onEditKeydown($event, scope.$index)"
                  />
                </template>
                <span v-else>{{ scope.row.asset_tag || '-' }}</span>
              </div>
            </template>
          </el-table-column>

          <!-- PSU Model Column -->
          <el-table-column prop="psu.model.name" label="PSU Model" min-width="120">
            <template #default="scope">
              <div
                class="scan-miners-dialog__editable-cell"
                @click="startEditCell(scope.$index, 'psu.model', scope.row.psu?.model)"
              >
                <template
                  v-if="
                    editingCellInfo &&
                    editingCellInfo.rowIndex === scope.$index &&
                    editingCellInfo.field === 'psu.model'
                  "
                >
                  <el-select
                    ref="editInput"
                    v-model="editingCellInfo.value"
                    filterable
                    clearable
                    size="small"
                    style="width: 100%"
                    value-key="valueKey"
                    @blur="saveEdit(scope.$index)"
                    @keydown="onEditKeydown($event, scope.$index)"
                  >
                    <el-option
                      v-for="option in psuModelOptions"
                      :key="option.value.valueKey"
                      :label="option.label"
                      :value="option.value"
                    >
                      <div class="w-full inline-flex justify-between items-center">
                        <p>{{ option.value.name }}</p>
                        <small v-if="option.value?.version" class="text-tertiary">{{ option.value.version }}</small>
                      </div>
                    </el-option>
                  </el-select>
                </template>
                <span v-else>
                  {{ scope.row['psu.model']?.name || scope.row['psu.model'] || '-' }}
                  <small v-if="scope.row['psu.model'] && scope.row['psu.model_version']" class="text-tertiary">
                    {{ scope.row['psu.model'].version ?? scope.row['psu.model_version'] }}
                  </small>
                </span>
              </div>
            </template>
          </el-table-column>

          <!-- PSU Serial Number Column -->
          <el-table-column prop="psu.sn" label="PSU Serial Number" min-width="150">
            <template #default="scope">
              <div
                class="scan-miners-dialog__editable-cell"
                @click="startEditCell(scope.$index, 'psu.sn', scope.row.psu?.sn)"
              >
                <template
                  v-if="
                    editingCellInfo && editingCellInfo.rowIndex === scope.$index && editingCellInfo.field === 'psu.sn'
                  "
                >
                  <el-input
                    ref="editInput"
                    v-model="editingCellInfo.value"
                    size="small"
                    @blur="saveEdit(scope.$index)"
                    @keydown="onEditKeydown($event, scope.$index)"
                  />
                </template>
                <span v-else>{{ scope.row['psu.sn'] || '-' }}</span>
              </div>
            </template>
          </el-table-column>

          <!-- MAC Address Column -->
          <el-table-column prop="mac_address" label="MAC Address" min-width="150">
            <template #default="scope">
              <div
                class="scan-miners-dialog__editable-cell"
                @click="startEditCell(scope.$index, 'mac_address', scope.row.mac_address)"
              >
                <template
                  v-if="
                    editingCellInfo &&
                    editingCellInfo.rowIndex === scope.$index &&
                    editingCellInfo.field === 'mac_address'
                  "
                >
                  <el-input
                    ref="editInput"
                    v-model="editingCellInfo.value"
                    v-maska
                    size="small"
                    data-maska="##:##:##:##:##:##"
                    data-maska-tokens="#:[0-9a-fA-F]"
                    @blur="saveEdit(scope.$index)"
                    @keydown="onEditKeydown($event, scope.$index)"
                  />
                </template>
                <span v-else>{{ scope.row.mac_address || '-' }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column width="70" fixed="right">
            <template #default="scope">
              <el-tooltip content="Remove" placement="top" effect="light">
                <el-button
                  link
                  title="Delete"
                  :loading="deleteLoadingIndex === scope.$index"
                  :disabled="deleteMinerMutation.isPending.value"
                  @click="handleDeleteMiner(scope.$index, scope.row)"
                >
                  <Icon icon="material-symbols:delete-outline" :height="16" />
                </el-button>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <template #footer>
      <div class="scan-miners-dialog__footer">
        <el-button v-if="hasEntries" size="large" type="danger" @click="confirmDiscardChanges">
          Discard Changes
        </el-button>
        <el-button size="large" @click="handleCloseDialog">Complete</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.scan-miners-dialog {
  max-height: 95vh;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
    flex-shrink: 0;

    &-title {
      margin: 0;
    }

    &-buttons {
      display: flex;
      gap: var(--spacing-spacing-sm);
    }
  }

  &__content {
    display: grid;
    grid-template-columns: 1fr 3fr;
    gap: var(--spacing-spacing-xl);
    padding: 20px;
    height: 100%;
  }

  &__form {
    border-right: 1px solid var(--border-color);
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-spacing-sm);

    &-content {
      flex: 1;
      overflow-y: auto;
    }

    &-add-button {
      width: 100%;
    }
  }

  &__table {
    overflow-y: auto;
    height: 100%;
    position: relative;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-spacing-md);
  }

  &__field-group {
    display: flex;
    gap: 8px;
    width: 100%;
  }

  &__field-input {
    flex: 1;
  }

  &__lock-button {
    padding: 8px;

    &.is-locked {
      color: var(--el-color-primary);
      background-color: var(--el-color-primary-light-8);
    }
  }

  &__editable-cell {
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 4px;

    &:hover {
      background-color: var(--el-fill-color-light);
    }
  }

  .el-dialog__body {
    height: calc(95vh - 40px);
    padding: 0;
    flex: 1;
    overflow: hidden;
  }

  .el-dialog__header {
    margin: 0;
    padding: 0;
    flex-shrink: 0;
  }
}

:deep(.el-table) {
  tr,
  th.el-table__cell {
    background-color: transparent;
  }
}
</style>
